import { useEditUserProfileMutation, useGetUserProfileQuery } from '@Features/Dashboard/DashboardSlice/Dashboard.slice'
import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { cloneDeep } from 'lodash'
import CustomTextInput from '@Components/InputFields/CustomTextInput/CustomTextInput'
import { Box, FlexLayout } from '@eduact/ed-system'
import { RaisedButton, TextInput } from '@eduact/ed-components'
import { useStudentScreenSize } from '@eduact/student-theme'
import EdTypography from '@Components/Utilities/EdTypography/EdTypography'
import { useProfileInfoPanel } from '../ProfileInfoPanelContext'
import { useProfileTabs } from '../ProfileTabsProvider'
import { FormButton, FormGrid, ProfileForm } from '../Profile.styled'
import { useTranslation } from 'react-i18next'

const schema = yup.object().shape({
	first_name: yup.string().required('field_required'),
	last_name: yup.string().required('field_required'),
	email: yup
		.string()
		.transform(v => (v === '' ? null : v))
		.nullable()
		.email('invalid_email'),
	phone_number: yup
		.string()
		.required('field_required')
		.matches(/^01[0,1,2,5]{1}[0-9]{8}/, 'invalid_phone'),
})

const ProfileBasicInfo = () => {
	const [t] = useTranslation('dashboard')
	const { data } = useGetUserProfileQuery()
	const { setIsDirty, blockedNavigationDone } = useProfileTabs()
	const {
		control,
		handleSubmit,
		reset,
		formState: { isDirty, isValid, isSubmitting },
	} = useForm<BasicInfoFormPayload>({
		mode: 'all',
		resolver: yupResolver(schema),
		defaultValues: cloneDeep({
			first_name: data?.data.first_name,
			last_name: data?.data.last_name,
			// `?? undefined`, never `?? ''` — an empty string is a VALUE, so RHF
			// marks the field dirty and submits it, and the API's email rule rejects
			// it for a student who simply never had one.
			email: data?.data.email ?? undefined,
			phone_number: data?.data.phone_number,
		}),
	})

	useEffect(() => {
		setIsDirty(isDirty)
	}, [isDirty])

	useEffect(() => {
		if (blockedNavigationDone) {
			reset()
		}
	}, [blockedNavigationDone, reset])
	const [update] = useEditUserProfileMutation()
	const handleSave = async (formData: BasicInfoFormPayload) => {
		try {
			const isSuccess = await update({ type: 'educational_info', data: { ...data?.data, ...formData } }).unwrap()
			if (isSuccess) {
				// Reset to the FORM's own shape. Spreading the whole StudentUser in
				// here fed reset() a dozen fields the form does not have; formData is
				// exactly what was just saved, which is what the form should now hold.
				reset(formData, { keepDirty: false, keepIsSubmitted: false })
			}
		} catch (error) {}
	}
	const handleCancel = () => reset()

	const { isDesktop } = useStudentScreenSize()
	const { setCurrentInfo, setPasswordModal } = useProfileInfoPanel()
	// console.log({ isDirty, isValid, isSubmitting })
	return (
		<ProfileForm onSubmit={handleSubmit(handleSave)}>
			<FlexLayout height={'100%'} width={'100%'} flexDirection={'column'}>
				<FormGrid width={'100%'} gridTemplateColumns={'1fr'}>
					<Box width="100%">
						<CustomTextInput width={'100%'} control={control} name="first_name" />
						<CustomTextInput width={'100%'} control={control} name="last_name" />
						<CustomTextInput width={'100%'} control={control} name="email" placeholder="email" />
						<CustomTextInput readOnly width={'100%'} control={control} name="phone_number" />
						<TextInput
							readOnly
							disabled
							width={'100%'}
							type="password"
							defaultValue={'12345'}
							background="transparent"
							withToggle={false}
							endAdornment={
								<span
									onClick={e => {
										setCurrentInfo('reset-password')
										setPasswordModal(true && !isDesktop)
									}}
								>
									<EdTypography  cursor='pointer' color="princetonOrange" textDecoration={'underline'} fontSize={{ sm: 8, md: 12 }}>
										{t('change_password')}
									</EdTypography>
								</span>
							}
						/>
					</Box>
					{!isDesktop && (
						<FlexLayout px={'2.063rem'} width="100%" justifyContent={'space-between'} alignItems="center">
							<FormButton type="button" onClick={handleCancel} disabled={!isDirty} variant="princetonOrange" btnSize={'small'}>
								{t('cancel')}
							</FormButton>
							<FormButton type="submit" disabled={!isDirty || !isValid || isSubmitting} btnSize={'small'}>
								{t('save')}
							</FormButton>
						</FlexLayout>
					)}
				</FormGrid>
				{isDesktop && (
					<FlexLayout justifyContent={'flex-end'} gridGap={'1rem'}>
						<RaisedButton onClick={handleCancel} type="button" disabled={!isDirty} variant="princetonOrange">
							{t('cancel')}
						</RaisedButton>
						<RaisedButton type="submit" disabled={!isDirty || !isValid || isSubmitting}>
							{t('save')}
						</RaisedButton>
					</FlexLayout>
				)}
			</FlexLayout>
		</ProfileForm>
	)
}

export default ProfileBasicInfo
